import { Capacitor } from "@capacitor/core";
import { StashtKeychain } from "@/plugins/StashtKeychain";
import StashtPreferences from "@/plugins/StashtPreferencesPlugin";

type TokenPayload = {
  accessToken: string;
  refreshToken: string | null;
};

const isNative = () => Capacitor.isNativePlatform();
const isIOS = () => isNative() && Capacitor.getPlatform() === "ios";
const isAndroid = () => isNative() && Capacitor.getPlatform() === "android";

export class SecureTokenStorage {
  private async withStorage<T>(operation: () => Promise<T>): Promise<T | null> {
    if (!isNative()) return null;
    try {
      return await operation();
    } catch (error) {
      console.error("Secure token storage failed:", error);
      return null;
    }
  }

  async saveTokens(accessToken: string, refreshToken: string | null): Promise<void> {
    if (!isNative()) return;

    await this.withStorage(async () => {
      if (isIOS()) {
        await StashtKeychain.saveTokens({ accessToken, refreshToken: refreshToken ?? undefined });
        return null;
      }

      if (isAndroid()) {
        await StashtPreferences.saveTokens({ accessToken, refreshToken: refreshToken ?? undefined });
        return null;
      }

      return null;
    });
  }

  async getTokens(): Promise<TokenPayload | null> {
    const tokens = await this.withStorage(async () => {
      if (isIOS()) {
        return await StashtKeychain.getTokens();
      }

      if (isAndroid()) {
        return await StashtPreferences.getTokens();
      }

      return { accessToken: null, refreshToken: null };
    });

    if (!tokens?.accessToken) {
      return null;
    }

    return {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken ?? null,
    };
  }

  async clearTokens(): Promise<void> {
    if (!isNative()) return;

    await this.withStorage(async () => {
      if (isIOS()) {
        await StashtKeychain.clearTokens();
        return null;
      }

      if (isAndroid()) {
        await StashtPreferences.clearTokens();
        return null;
      }

      return null;
    });
  }
}

export const secureTokenStorage = new SecureTokenStorage();
